import React, { useState, useEffect } from "react";
import {
  Table,
  Avatar,
  Tag,
  Spin,
  Alert,
  Typography,
  Descriptions,
  List,
  Row,
  Col,
  Card,
  Tabs,
  Timeline,
  Space,
  Button,
  Divider,
  Drawer,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Tooltip,
} from "antd";
import {
  UserOutlined,
  LinkOutlined,
  IdcardOutlined,
  ApartmentOutlined,
  BookOutlined,
  SettingOutlined,
  CodeOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusCircleOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
  SolutionOutlined,
} from "@ant-design/icons";

const { Title, Text, Link, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;
const { confirm } = Modal;

// --- CONFIGURATION ---
const API_BASE_URL = "https://service.ireedindia.com/v1";
const SEARCH_API_URL = `${API_BASE_URL}/users/search`;
const ADD_USER_API_URL = `${API_BASE_URL}/add-users`;
const EDIT_USER_API_URL_BASE = `${API_BASE_URL}/edit-user`;
const DELETE_USER_API_URL_BASE = `${API_BASE_URL}/delete-user`;
const IMAGE_BASE_URL = "https://service.ireedindia.com/image/";

// --- Interfaces ---
export enum UserRole {
  ADMIN = 'admin',
  STUDENT = 'student',
  CONTENT_WRITER = 'content_writer',
  COMPANY = 'company',
  COMPANY_AGENT = 'company_agent',
}

interface Employment { company?: string; role?: string; startDate?: string; endDate?: string; description?: string; }
interface Education { qualification?: string; boardOrUniversity?: string; year?: string; details?: string; }
interface Project { title?: string; technologies?: string[]; link?: string; description?: string; }
interface CareerProfile { industry?: string; department?: string; roleCategory?: string; jobRole?: string; }
interface PersonalDetails { gender?: string; maritalStatus?: string; dob?: string; category?: string; differentlyAbled?: boolean; careerBreak?: boolean; workPermit?: string; address?: string; }
interface Language { language: string; proficiency: string; read: boolean; write: boolean; speak: boolean; }

export interface User {
  _id: string;
  key: string;
  name: string;
  role: UserRole;
  isVerified: boolean;
  emailId: string;
  mobile: string;
  userProfile?: string;
  resume?: string;
  location?: string;
  workStatus?: string;
  yearsOfExperience?: string;
  availabilityToJoin?: string;
  resumeHeadline?: string;
  keySkills?: string[];
  employment?: Employment[];
  education?: Education[];
  projects?: Project[];
  profileSummary?: string;
  careerProfile?: CareerProfile;
  desiredJobType?: string;
  desiredEmploymentType?: string;
  preferredShift?: string;
  preferredWorkLocation?: string;
  expectedSalary?: string;
  personalDetails?: PersonalDetails;
  languages?: Language[];
  allowedMenuKeys?: string[];
}

// --- UserDetailView Component ---
const UserDetailView: React.FC<{ user: User }> = ({ user: record }) => {
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  };
  const renderBooleanTag = (value: boolean | undefined) =>
    value ? <Tag icon={<CheckCircleOutlined />} color="success">Yes</Tag> : <Tag icon={<CloseCircleOutlined />} color="error">No</Tag>;

  return (
    <Tabs defaultActiveKey="1" type="card">
      <TabPane tab={<span><IdcardOutlined /> Summary</span>} key="1">
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={16}>
            <Card title="Professional Summary">
              <Title level={5}>{record.resumeHeadline || "N/A"}</Title>
              <Paragraph type="secondary">{record.profileSummary || "No summary provided."}</Paragraph>
              <Divider />
              <Title level={5}>Key Skills</Title>
              <Space size={[0, 8]} wrap>
                {record.keySkills && record.keySkills.length > 0 ? record.keySkills.map((skill) => <Tag color="blue" key={skill}>{skill}</Tag>) : "No skills listed."}
              </Space>
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card title="Quick Info">
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Experience">{record.yearsOfExperience || "N/A"}</Descriptions.Item>
                <Descriptions.Item label="Work Status"><Tag color="purple">{record.workStatus || "N/A"}</Tag></Descriptions.Item>
                <Descriptions.Item label="Availability">{record.availabilityToJoin || "N/A"}</Descriptions.Item>
                <Descriptions.Item label="Resume">
                  {record.resume ? <Link href={record.resume} target="_blank"><LinkOutlined /> View/Download</Link> : "N/A"}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>
      </TabPane>
      <TabPane tab={<span><ApartmentOutlined /> Experience & Projects</span>} key="2">
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Employment History">
              {record.employment?.length ? (
                <Timeline>
                  {record.employment.map((emp, i) => (
                    <Timeline.Item key={i} dot={<ClockCircleOutlined />}>
                      <Title level={5}>{emp.role || "Role"}</Title>
                      <Text strong>{emp.company || "Company"}</Text><br />
                      <Text type="secondary">{emp.startDate || "?"} - {emp.endDate || "Present"}</Text>
                      <Paragraph>{emp.description}</Paragraph>
                    </Timeline.Item>
                  ))}
                </Timeline>
              ) : "No employment history."}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Projects">
              {record.projects?.length ? (
                <List dataSource={record.projects} renderItem={(proj) => (
                  <List.Item>
                    <List.Item.Meta avatar={<Avatar icon={<CodeOutlined />} />} title={proj.link ? <Link href={proj.link} target="_blank">{proj.title}</Link> : proj.title} description={<Space size={[0, 4]} wrap>{proj.technologies?.map(t => <Tag key={t}>{t}</Tag>)}</Space>} />
                    <Paragraph>{proj.description}</Paragraph>
                  </List.Item>
                )}
                />
              ) : "No projects."}
            </Card>
          </Col>
        </Row>
      </TabPane>
      <TabPane tab={<span><BookOutlined /> Education & Skills</span>} key="3">
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Education">
              {record.education?.length ? (
                <Timeline>
                  {record.education.map((edu, i) => (
                    <Timeline.Item key={i}>
                      <Title level={5}>{edu.qualification}</Title>
                      <Text strong>{edu.boardOrUniversity}</Text><br />
                      <Text type="secondary">Year: {edu.year}</Text>
                      <Paragraph>{edu.details}</Paragraph>
                    </Timeline.Item>
                  ))}
                </Timeline>
              ) : "No education."}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Languages">
              {record.languages?.length ? <List dataSource={record.languages} renderItem={(lang) => <List.Item><List.Item.Meta title={lang.language} description={`Proficiency: ${lang.proficiency}`} /><Space>{renderBooleanTag(lang.read)} Read {renderBooleanTag(lang.write)} Write {renderBooleanTag(lang.speak)} Speak</Space></List.Item>} /> : "No languages."}
            </Card>
          </Col>
        </Row>
      </TabPane>
      <TabPane tab={<span><SettingOutlined /> Preferences</span>} key="4">
        <Row gutter={16}>
          <Col span={12}><Card title="Career Profile" bordered={false}><Descriptions column={1} size="small" bordered><Descriptions.Item label="Industry">{record.careerProfile?.industry}</Descriptions.Item><Descriptions.Item label="Role">{record.careerProfile?.jobRole}</Descriptions.Item></Descriptions></Card></Col>
          <Col span={12}><Card title="Desired Job" bordered={false}><Descriptions column={1} size="small" bordered><Descriptions.Item label="Type">{record.desiredJobType}</Descriptions.Item><Descriptions.Item label="Location">{record.preferredWorkLocation}</Descriptions.Item><Descriptions.Item label="Salary">{record.expectedSalary}</Descriptions.Item></Descriptions></Card></Col>
        </Row>
      </TabPane>
      <TabPane tab={<span><UserOutlined /> Personal</span>} key="5">
        <Card title="Personal Details" bordered={false}>
          <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
            <Descriptions.Item label="DOB">{formatDate(record.personalDetails?.dob)}</Descriptions.Item>
            <Descriptions.Item label="Gender">{record.personalDetails?.gender}</Descriptions.Item>
            <Descriptions.Item label="Mobile">{record.mobile}</Descriptions.Item>
            <Descriptions.Item label="Address">{record.personalDetails?.address}</Descriptions.Item>
          </Descriptions>
        </Card>
      </TabPane>
    </Tabs>
  );
};

// --- Main Component ---
const UserDisplayTable: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination & Filtering State
  const [pagination, setPagination] = useState({ current: 1, pageSize: 1000, total: 0 });
  const [searchText, setSearchText] = useState("");
  // Default to ADMIN & CONTENT_WRITER as requested
  const [filteredRoles, setFilteredRoles] = useState<UserRole[]>([UserRole.ADMIN, UserRole.CONTENT_WRITER]);

  // Drawer & Modal State
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<User | null>(null);
  const [form] = Form.useForm();

  // --- Helper: Get Token ---
  const getAuthToken = () => {
    const storedDetails = localStorage.getItem("userDetails");
    return storedDetails ? JSON.parse(storedDetails)?.accessToken : null;
  };

  const fetchUsers = async () => {
    setLoading(true); setError(null);
    const token = getAuthToken();
    try {
      const response = await fetch(SEARCH_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { "Authorization": `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          pageNumber: (pagination.current - 1).toString(),
          size: pagination.pageSize.toString(),
          role: filteredRoles,
          search: searchText || undefined,
        }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();

      let rawData = [];
      let totalElements = 0;

      if (Array.isArray(result)) {
        rawData = result;
        totalElements = result.length;
      } else if (result.data && Array.isArray(result.data)) {
        rawData = result.data;
        totalElements = result.totalElements || result.data.length;
      } else if (result.data && result.data.content && Array.isArray(result.data.content)) {
        rawData = result.data.content;
        totalElements = result.data.totalElements || 0;
      }

      setUsers(rawData.map((user: any) => ({
        ...user,
        key: user._id,
        userProfile: user.userProfile ? `${IMAGE_BASE_URL}${user.userProfile}` : undefined,
        resume: user.resume ? `${IMAGE_BASE_URL}${user.resume}` : undefined,
      })));
      setPagination((prev) => ({ ...prev, total: totalElements }));
    } catch (err: any) {
      setError(err.message || "Failed to fetch data");
    } finally {
      setLoading(false); setInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [pagination.current, pagination.pageSize, filteredRoles, searchText]);

  // --- DELETE ---
  const handleDelete = async (userId: string) => {
    const token = getAuthToken();
    if (!token) return message.error("Please log in.");
    try {
      const response = await fetch(`${DELETE_USER_API_URL_BASE}/${userId}`, {
        method: "DELETE", headers: { "Authorization": `Bearer ${token}` }
      });
      if (response.ok) { message.success("User deleted"); fetchUsers(); }
      else { message.error((await response.json()).message || "Delete failed"); }
    } catch { message.error("Network error"); }
  };

  const showDeleteConfirm = (userId: string) => {
    confirm({
      title: "Are you sure you want to delete this user?",
      icon: <ExclamationCircleOutlined />,
      okText: "Yes", okType: "danger", cancelText: "No",
      onOk() { handleDelete(userId); },
    });
  };

  // --- ADD / EDIT ---
  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();
      const token = getAuthToken();
      if (!token) return message.error("Please log in.");
      setModalLoading(true);

      const isEdit = !!selectedUserForEdit;
      const url = isEdit ? `${EDIT_USER_API_URL_BASE}/${selectedUserForEdit._id}` : ADD_USER_API_URL;
      const method = isEdit ? "PATCH" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(values),
      });
      const result = await response.json();
      if (response.ok) {
        message.success(isEdit ? "User updated" : "User added");
        setModalVisible(false); form.resetFields(); fetchUsers();
      } else {
        message.error(result.message || "Operation failed");
      }
    } catch (error) { console.error(error); } finally { setModalLoading(false); }
  };

  // --- TABLE CHANGE (Handles Pagination & Filter clicks) ---
  const handleTableChange = (newPagination: any, filters: any, sorter: any) => {
    setPagination(prev => ({ ...prev, current: newPagination.current, pageSize: newPagination.pageSize }));
    if (filters.role) {
      setFilteredRoles(filters.role);
      if (pagination.current !== 1) setPagination(prev => ({ ...prev, current: 1 }));
    }
  };

  // --- COLUMNS ---
  const columns = [
    {
      title: "Profile",
      dataIndex: "userProfile",
      key: "userProfile",
      width: 80,
      render: (url: string) => (
        <Avatar
          src={url}
          icon={<UserOutlined />}
          size="large"
          style={{ border: "2px solid #e8e8e8", boxShadow: "0 2px 6px rgba(0,0,0,0.1)" }}
        />
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 180,
      sorter: (a: User, b: User) => (a.name || "").localeCompare(b.name || ""),
      defaultSortOrder: "ascend",
      render: (text: string) => (
        <Text strong style={{ color: "#1677ff" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Email",
      dataIndex: "emailId",
      key: "emailId",
      width: 230,
      render: (email: string) => (
        <a href={`mailto:${email}`} style={{ color: "#555" }}>
          {email}
        </a>
      ),
    },
    {
      title: "Mobile",
      dataIndex: "mobile",
      key: "mobile",
      width: 140,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 150,
      filters: [
        { text: "Admin", value: UserRole.ADMIN },
        // { text: "Student", value: UserRole.STUDENT },
        { text: "Content Writer", value: UserRole.CONTENT_WRITER },
        { text: "Company", value: UserRole.COMPANY },
        { text: "Company Agent", value: UserRole.COMPANY_AGENT }, // ✅ Added
      ],
      filteredValue: filteredRoles,
      render: (role: UserRole) => {
        const color =
          role === UserRole.ADMIN
            ? "volcano"
            : role === UserRole.STUDENT
              ? "green"
              : role === UserRole.COMPANY_AGENT
                ? "orange"
                : "geekblue";
        return <Tag color={color}>{role.toUpperCase()}</Tag>;
      },
    },
    {
      title: "Verified",
      dataIndex: "isVerified",
      key: "isVerified",
      align: "center" as const,
      width: 140,
      render: (verified: boolean) =>
        verified ? (
          <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontWeight: 500 }}>
            Active User
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="error" style={{ fontWeight: 500 }}>
            Deactive User
          </Tag>
        ),
    },
    {
      title: "Menu Access",
      dataIndex: "allowedMenuKeys",
      key: "allowedMenuKeys",
      width: 300,
      render: (menus: string[]) =>
        menus && menus.length > 0 ? (
          <Space size={[0, 8]} wrap>
            {menus.map((menu) => (
              <Tag color="blue" key={menu}>
                {menu}
              </Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary">No Access</Text>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right" as const,
      width: 140,
      render: (_: any, record: User) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => { setSelectedUser(record); setDrawerVisible(true); }}
            />
          </Tooltip>
          <Tooltip title="Edit User">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedUserForEdit(record);
                form.setFieldsValue(record);
                setModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Delete User">
            <Button
              type="link"
              icon={<DeleteOutlined />}
              danger
              onClick={() => showDeleteConfirm(record._id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (initialLoading) return <div style={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}><Spin size="large" tip="Loading..." /></div>;

  return (
    <div style={{ padding: "24px" }}>
      <div className="flex justify-between mb-4 items-center">
        <div>
          <Title level={2} style={{ margin: 0 }}>User Management</Title>
          <Text type="secondary">Manage users, roles, and permissions.</Text>
        </div>
        <Space>
          <Input.Search
            placeholder="Search by name, email or mobile"
            onSearch={(value) => {
              setSearchText(value);
              setPagination(prev => ({ ...prev, current: 1 }));
            }}
            onChange={(e) => {
              if (!e.target.value) {
                setSearchText("");
                setPagination(prev => ({ ...prev, current: 1 }));
              }
            }}
            style={{ width: 300 }}
            allowClear
            enterButton
          />
          <Button type="primary" icon={<PlusCircleOutlined />} onClick={() => { setSelectedUserForEdit(null); form.resetFields(); setModalVisible(true); }}>
            Add User
          </Button>
        </Space>
      </div>

      {error && <Alert message={error} type="error" showIcon className="mb-4" />}

      <Table
        columns={columns as any}
        dataSource={users}
        rowKey="key"
        scroll={{ x: 1200 }}
        bordered
        loading={loading}
        onChange={handleTableChange}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["20", "40", "100", "500", "1000"],
          showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total}`,
        }}
        style={{
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          padding: 12,
        }}
        rowClassName={() => "custom-table-row"}
      />

      <Drawer title={selectedUser?.name || "Details"} width="70%" onClose={() => setDrawerVisible(false)} visible={drawerVisible} destroyOnClose>
        {selectedUser && <UserDetailView user={selectedUser} />}
      </Drawer>

      <Modal title={selectedUserForEdit ? "Edit User" : "Add User"} open={modalVisible} onOk={handleModalSubmit} onCancel={() => setModalVisible(false)} confirmLoading={modalLoading} destroyOnClose>
        <Form form={form} layout="vertical" initialValues={{ isVerified: false }}>
          <Form.Item label="Name" name="name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item label="Email" name="emailId" rules={[{ required: true, type: "email" }]}><Input /></Form.Item>
          <Form.Item label="Mobile" name="mobile" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item label="Role" name="role" rules={[{ required: true }]}>
            <Select>
              <Option value={UserRole.ADMIN}>Admin</Option>
              {/* <Option value={UserRole.STUDENT}>Student</Option> */}
              <Option value={UserRole.CONTENT_WRITER}>Content Writer</Option>
              <Option value={UserRole.COMPANY}>Company</Option>
              <Option value={UserRole.COMPANY_AGENT}>Company Agent</Option> {/* ✅ Added */}
            </Select>
          </Form.Item>
          <Form.Item label="Verified" name="isVerified" valuePropName="checked">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
          <Form.Item label="Menu Access" name="allowedMenuKeys">
            <Select mode="multiple" placeholder="Select menus">
              <Option value="dashboard">Dashboard</Option>
              <Option value="users">Users</Option>
              <Option value="MarketAnalaysis">Market Analysis</Option>
              <Option value="developers">Developers</Option>
              <Option value="projects">Projects</Option>
              <Option value="courses">Courses</Option>
              <Option value="blog">Blog</Option>
              <Option value="artical">Articles</Option>
              <Option value="success">Success</Option>
              <Option value="keyword">Keyword</Option>
              <Option value="home">Home</Option>
              <Option value="job">Job</Option>
              <Option value="builder">Builder</Option>
              <Option value="settings">Settings</Option>
              <Option value="events">Events</Option>
              <Option value="audience">Audience</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserDisplayTable;